/**
 * A number that counts to its value (Living Valley motion spec).
 *
 * On first show it counts up from zero over 1400ms, easeOutCubic. When the
 * value changes afterwards -- money added on this screen, or on another
 * device -- it counts from the old value to the new one, so a parent SEES the
 * fund move instead of a figure silently swapping.
 *
 * React always renders the FINAL text. The animation only rewrites the
 * element's text between frames and finishes on the same string React drew,
 * so screen readers, tests and anything reading the DOM after the fact get the
 * real figure. Under prefers-reduced-motion, in jsdom, or with no
 * requestAnimationFrame, nothing animates at all.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';

const DURATION = 1400;

/**
 * What each named figure last showed, for the life of the page. Without it,
 * every visit to Home counted the fund up from zero again -- charming once,
 * tiresome by the third tab switch (audit, 2026-10-12). A figure with an `id`
 * counts from zero the first time only, then from wherever it last stood.
 */
const lastShown = new Map<string, number>();

function motionAllowed(): boolean {
  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') return false;
  if (/jsdom/i.test(navigator.userAgent)) return false;
  return !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export function CountUp({
  value,
  format,
  className,
  from = 0,
  id,
}: {
  /** Remember this figure across remounts; see `lastShown`. */
  id?: string;
  /** In the same units `format` takes (cents, for `dollars`). */
  value: number;
  format: (n: number) => string;
  className?: string;
  /** Where the first count starts. */
  from?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const shown = useRef<number | null>(null);
  const text = format(value);

  // Before paint, so the first frame is never the final figure flashing.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const start = shown.current ?? (id !== undefined ? lastShown.get(id) : undefined) ?? from;
    shown.current = value;
    if (id !== undefined) lastShown.set(id, value);
    if (!motionAllowed() || start === value) {
      el.textContent = text;
      return;
    }
    let frame = 0;
    const began = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - began) / DURATION);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = p >= 1 ? text : format(Math.round(start + (value - start) * eased));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    el.textContent = format(start);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      el.textContent = text;
    };
    // `format` is a module function at every call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => () => void (shown.current = null), []);

  return (
    <span ref={ref} className={className}>
      {text}
    </span>
  );
}
